import { STICKY_THROW } from '../shared/sticky.ts';
import { BeastKind, LUNGE_REACH, wyrm } from '../shared/wildlife.ts';
import { DEFENDERS } from '../shared/siege.ts';
import { BTN_FIRE, BTN_LEFT, BTN_RIGHT, BTN_SCOPE, BTN_UP } from '../shared/actor.ts';
import { ClassId, DROID_MASK, stumps } from '../shared/body.ts';
import { ACTOR_H, GRAVITY } from '../shared/constants.ts';
import { PICKUP_R, PRIMARIES, invByte } from '../shared/items.ts';
import { CallKind, Evac, Phase, Team, callCost, quantizeAim } from '../shared/protocol.ts';
import { MAT_HARD, Mat, GEM_CRYSTAL_VALUE, RARE_EARTH_VALUE } from '../shared/materials.ts';
import { Rng } from '../shared/rng.ts';
import { DIGGER_REACH, LASER_MAX, PROJ, SHOULDER_X, SHOULDER_Y, WEAPONS, WeaponId } from '../shared/weapons.ts';
import { CANNON_SPEED, SMG_SPEED, TANK_STEP_UP, type Tank, isDog, isPet, isSpider, surfCapacity, tankH, tankW, isMole } from '../shared/tank.ts';
import { ENGINE_NOZZLE_Y, ENGINE_X, SHIP_H, SHIP_W, ShipPart, hasShipPart, shipPoint } from '../shared/dropship.ts';
import type { InputCmd, Player, World } from './world.ts';
import { COLS, SHAFT_HALF } from '../shared/dungeon.ts';
import { cellCentreX, cellOfFeet, inShaftUnder, mazeDistances, nextHop } from './maze.ts';
import { type Ramp, caveLevel, onPassage } from '../shared/caves.ts';
import { NAV_CELL, type NavBody, type NavPath, findPath, navBudget, undiggable } from './nav.ts';

/** Names for bots (shown with a BOT tag). */
const NAMES = [
  'Vasquez', 'Hicks', 'Drake', 'Apone', 'Ferro', 'Dietrich', 'Frost', 'Wierzbowski', 'Crowe', 'Spunkmeyer',
  'Gorman', 'Hudson', 'Ripley', 'Bishop', 'Dutch', 'Blain', 'Mac', 'Billy', 'Poncho', 'Hawkins',
  'Rico', 'Dizzy', 'Ace', 'Zim', 'Sugar', 'Flores', 'Kitten', 'Shujumi', 'Brutus', 'Carmen',
];

export function botName(rng: Rng): string {
  return `BOT ${NAMES[rng.int(NAMES.length)]}`;
}

/** Fighting distance by weapon: close enough to hit, far enough to live. */
const RANGE: Record<number, number> = {
  [WeaponId.Rifle]: 130,
  [WeaponId.Bazooka]: 170,
  [WeaponId.Grenade]: 110,
  [WeaponId.Sticky]: 90,
  [WeaponId.Sniper]: 280,
  [WeaponId.Digger]: 0,
  [WeaponId.Shotgun]: 55,
  [WeaponId.GrenadeLauncher]: 150,
  [WeaponId.Gatling]: 140,
  [WeaponId.Laser]: 230,
  [WeaponId.ATCannon]: 240,
  [WeaponId.LightRifle]: 200,
  [WeaponId.Smg]: 85,
  [WeaponId.Autocannon]: 160,
  [WeaponId.Blaster]: 150,
};
const MAX_SHOT = 340; // won't shoot at anything further than this
/** Anti-air: how far off a bot will shoot at an enemy dropship, and with what (not grenades, tools or the radio). */
const AA_RANGE = 360;
const ANTI_AIR: number[] = [WeaponId.Rifle, WeaponId.Bazooka, WeaponId.Sniper, WeaponId.Shotgun, WeaponId.GrenadeLauncher, WeaponId.Gatling, WeaponId.Laser, WeaponId.ATCannon, WeaponId.LightRifle, WeaponId.Smg, WeaponId.Autocannon, WeaponId.Blaster];
/** How far around itself a prospecting bot looks for gold (half-width, and depth from just above its head). */
const GOLD_SCAN_W = 220;
const GOLD_SCAN_H = 280;
/** Gold cells (of 25 sampled around it) that make a seam worth digging for. */
const GOLD_SEAM = 8;
/** The chance, each life, that a bot takes on the prospecting objective. */
const PROSPECT_CHANCE = 0.15;
/** Hard cells (sampled every other row) over a seam that make it not worth the dig. */
const GOLD_HARD = 3;
/**
 * The gold scan is the costliest thing a bot thinks about: at most this many
 * bots in the room rescan in any one tick (the rest keep what they had and
 * try again next tick), and each looks closely at no more than
 * GOLD_CANDIDATES of the ore cells it finds.
 */
const GOLD_SCANS_PER_TICK = 2;
const GOLD_CANDIDATES = 40;
/** The scans made this tick (World.tick), across every bot in the room. */
const goldScans = new WeakMap<World, { tick: number; n: number }>();
/** Ticks of digging with nothing to show for it before a seam is given up. */
const GOLD_STALL = 120;
/**
 * Bot skill, 1 (beginner) to 5 (expert), each bot its own (Player.skill,
 * dealt by World.dealSkills): how far off its aim wanders, how long it takes
 * to react to a new target, how well it leads a moving one, how far off it'll
 * take a shot, and how often it hesitates on the trigger. 3 is the bots as
 * they always were.
 */
const SKILL = [
  null,
  { noise: 2.6, react: 2.6, lead: 0.15, range: 210, pause: 0.5 },
  { noise: 1.7, react: 1.7, lead: 0.55, range: 270, pause: 0.25 },
  { noise: 1, react: 1, lead: 1, range: 340, pause: 0 },
  { noise: 0.6, react: 0.6, lead: 1, range: 400, pause: 0 },
  { noise: 0.3, react: 0.35, lead: 1, range: 470, pause: 0 },
] as const;
const skillOf = (p: Player) => SKILL[Math.max(1, Math.min(5, p.skill))]!;

/** With this many enemies left or fewer, prospectors stop digging and fight. */
const ENDGAME_FOES = 4;

/**
 * A server-side bot: it reads the world directly (no network) and emits the
 * same input command a client would, so it plays by exactly the same rules:
 * same rate of fire, magazines, inventory, rockets. Thinking is cheap and
 * staggered: target choice every half second, line of sight every few
 * ticks, steering and aim every tick.
 */
/** A bot's odds of saving for each radio call, by its skill (1-5): the better it is, the likelier the tarantula. */
export function wishOdds(skill: number): [number, number][] {
  return [
    [CallKind.Watchdog, 3],
    [CallKind.Mole, 2],
    [CallKind.Tank, 2],
    [CallKind.Dropship, 2],
    [CallKind.Tarantula, skill >= 4 ? 1.5 : skill === 3 ? 0.7 : 0.25],
  ];
}

/** How near a creature has to come (from the shoulder) for a bot to turn its gun on it. */
const BEAST_REACT = 240;
/** Extermination: what it goes and gets to fight a basilisk with (its rifle barely scratches one). */
const HEAVY: number[] = [WeaponId.Bazooka, WeaponId.ATCannon, WeaponId.Autocannon];
/** What a cell's worth to a bot digging for gold (rare earth ten times gold). */
const oreWorth = (m: number) => (m === Mat.Gold ? 1 : m === Mat.GemCrystal ? GEM_CRYSTAL_VALUE : m === Mat.RareEarth ? RARE_EARTH_VALUE : 0);

export class BotBrain {
  private readonly rng: Rng;
  /** Aim error (radians) and how often it hesitates: per-bot skill. */
  private readonly noise: number;
  private target = -1;
  private seeTarget = false;
  private strafe = 1;
  private strafeUntil = 0;
  private stuck = 0;
  /** On foot: the nearest it has come to where it's going, and when; and a detour when that stalls. */
  /** Map sense: the path it's following (nav.ts), how far along, and when and toward where it was planned. */
  private path: NavPath | null = null;
  private pathAt = 0;
  private pathIdx = 0;
  private pathTo = { x: 0, y: 0 };
  /** Following a path but getting nowhere: where it was and since when, and a spell off the path to work itself loose. */
  private routeX = 0;
  private routeY = 0;
  private routeAt = 0;
  private offRouteUntil = 0;
  /** Where its path last got stuck (planned round next time), and how often in a row. */
  private avoid: { x: number; y: number; r: number } | undefined;
  private stalls = 0;
  private walkBest = Infinity;
  private walkAt = 0;
  private detourUntil = 0;
  private detourDir = 0;
  /** Tank surfing: ticks its ride has been heading away from the fight, or standing still. */
  private surfAway = 0;
  private surfStill = 0;
  /** Ticks a nearby target has been out of sight (a floor or wall between us). */
  private blind = 0;
  /** Extermination: can it see the basilisk it's after, and how long it's been close by with no line on it (in the rock: dig to it). */
  private seeQuarry = false;
  private quarryBlind = 0;
  private lastX = 0;
  private lastY = 0;
  private trigger = false;
  private seq = 0;
  private nadeUntil = 0;
  /** Until when it has a sticky charge out to throw (sticky.ts): up close, mostly at someone in a vehicle. */
  private stickyUntil = 0;
  /** Laying a landmine until this tick. */
  private mineUntil = 0;
  /** Not before this tick: a beat to get bearings after landing, and to react to a new target. */
  private holdFire = 0;
  private wasAlive = false;
  private readonly react: number;
  private readonly phase: number;
  /** Some bots go for a tank when one lands near them. */
  private readonly tanker: boolean;
  /** Regicide: some bots go straight for the enemy king; the rest fight whoever is nearest. */
  private readonly assault: boolean;
  /** Ticks driving without getting anywhere (it shells the way clear, then bails out). */
  private tankStuck = 0;
  /** Distance to the target at the last progress check (driving). */
  private progD = Infinity;
  /** A tank it gave up on, left alone until `abandonUntil`. */
  private abandoned = -1;
  private abandonUntil = 0;
  /** Extraction: distances through the labyrinth to the current goal, what goal they're for, and when they were worked out. */
  private navDist: Int16Array | null = null;
  private navKey = '';
  private navAt = -1e9;
  /** How long to charge the laser before letting go (ticks). */
  private laserGoal = 30;
  /** Extraction: resting on a ledge to refill the jetpack before the next climb. */
  private resting = false;
  /**
   * A rare objective, rolled each life: go prospecting. Dig down to real
   * gold whenever the fighting's elsewhere, to buy a dropship.
   */
  private prospector = false;
  /**
   * What it's saving up for on the radio (CallKind): a watchdog, a mole, a
   * tank, a dropship or a tarantula, picked by the odds in `wishOdds` and
   * picked again once bought, or when it can't be had (it has one already;
   * no room for another).
   */
  private wish = -1;
  /** The tank or mole it called in, which it then goes and climbs into (tank slot; -1 none). */
  private myRide = -1;
  /** The gold cell it's digging toward, and when it last looked for one. */
  private gold: { x: number; y: number } | null = null;
  private goldScanAt = -1e9;
  /** Mining progress: gold banked when it last went up, and when. Seams it gave up on (rock in the way). */
  private goldBanked = 0;
  private goldGainAt = 0;
  private readonly badGold: { x: number; y: number }[] = [];
  /** Anti-air: the enemy dropship point it's shooting at (re-picked a few times a second), and its velocity. */
  private air: { x: number; y: number; vx: number; vy: number } | null = null;
  /** A creature coming at it (wildlife.ts), nearer than whoever it's fighting: the body point to shoot at. */
  private beast: { x: number; y: number } | null = null;
  private readonly airPt = { x: 0, y: 0 };
  /** Is the path it's on planned for a vehicle (else for itself on foot)? */
  private pathVeh = false;

  constructor(seed: number) {
    this.rng = new Rng(seed);
    this.noise = 0.06 + this.rng.next() * 0.12;
    this.phase = this.rng.int(15);
    this.react = 15 + this.rng.int(25);
    this.tanker = this.rng.next() < 0.5;
    this.assault = this.rng.next() < 0.5;
  }

  /**
   * Real gold to dig for: the nearest seam (a cell of gold with more around
   * it, not a stray grain) under or beside us, as deep as a long shaft
   * reaches. Walking there counts for less than digging down to it, and
   * gold above (which would mean tunnelling up) for more. Re-scanned every
   * few seconds, or as soon as the cell it was after is dug out.
   */
  /**
   * The next thing to save for. Watchdogs most (cheap, and they fight
   * alongside), then a mole, a tank or a dropship, alike; a tarantula, the
   * dearest by far, is mostly for the better bots. Never one it can't have.
   */
  private pickWish(p: Player, can: (kind: number) => boolean): number {
    const odds = wishOdds(p.skill);
    let total = 0;
    for (const [kind, w] of odds) if (can(kind)) total += w;
    if (total <= 0) return -1;
    let r = this.rng.next() * total;
    for (const [kind, w] of odds) {
      if (!can(kind)) continue;
      r -= w;
      if (r <= 0) return kind;
    }
    return -1;
  }

  private findGold(world: World, p: Player): { x: number; y: number } | null {
    const t = world.tick;
    const g = this.gold;
    // (Each its own rhythm, so they don't all rescan in step.)
    const jitter = this.phase * 2;
    if (g && oreWorth(world.terrain.get(g.x, g.y)) > 0 && t - this.goldScanAt < 150 + jitter) return g;
    if (g === null && t - this.goldScanAt < 45 + jitter) return null;
    let budget = goldScans.get(world);
    if (!budget || budget.tick !== t) goldScans.set(world, (budget = { tick: t, n: 0 }));
    if (budget.n >= GOLD_SCANS_PER_TICK) return g && oreWorth(world.terrain.get(g.x, g.y)) > 0 ? g : null;
    budget.n++;
    this.goldScanAt = t;
    this.gold = null;
    const ter = world.terrain;
    let best = Infinity;
    let looked = 0;
    const x0 = Math.floor(p.cx) - GOLD_SCAN_W;
    const y0 = Math.floor(p.cy) - 30;
    for (let y = y0; y < y0 + GOLD_SCAN_H; y += 3) {
      for (let x = x0; x < x0 + GOLD_SCAN_W * 2; x += 3) {
        if (oreWorth(ter.get(x, y)) === 0 || this.badGold.some((b) => Math.abs(b.x - x) < 14 && Math.abs(b.y - y) < 14)) continue;
        const d = Math.abs(x - p.cx) + Math.max(0, y - p.cy) * 1.3 + Math.max(0, p.cy - y) * 3;
        if (d >= best) continue;
        if (++looked > GOLD_CANDIDATES) return this.gold;
        // A seam, not a speck: enough gold around it to be worth the dig (a rare earth crystal, small as it is, always is).
        let n = 0;
        for (let yy = y - 4; yy <= y + 4; yy += 2) for (let xx = x - 4; xx <= x + 4; xx += 2) n += oreWorth(ter.get(xx, yy));
        if (n < GOLD_SEAM) continue;
        // Dug for from above: skip it under a bunker (concrete, steel) or
        // stone that never yields; rock in the way only makes it a longer dig.
        let built = 0;
        let rock = 0;
        for (let yy = y - 2; yy > ter.surfaceY(x) - 1 && built <= GOLD_HARD; yy -= 2) {
          const m = ter.get(x, yy);
          if (m === Mat.Rock) rock++;
          else if (MAT_HARD[m]) built++;
        }
        if (built > GOLD_HARD || d + rock * 6 >= best) continue;
        best = d + rock * 6;
        this.gold = { x, y };
      }
    }
    return this.gold;
  }

  /**
   * Anti-air: the best point to shoot on an enemy dropship in sight and
   * reach, if it's nearer than the clone it's fighting (`fight`): an engine
   * pod still on its pylon first (they hang out, and losing two brings it
   * down), else the hull.
   */
  /** Is an enemy dropship within anti-air range above it (line of sight or not)? */
  private airThreat(world: World, p: Player): boolean {
    for (const sh of world.ships) {
      if (!sh || sh.leaving || sh.owner === p.id || (p.team !== Team.None && sh.team === p.team)) continue;
      const cx = sh.x + SHIP_W / 2;
      const cy = sh.y + SHIP_H / 2;
      if (cy < p.cy && Math.hypot(cx - p.cx, cy - p.cy) < AA_RANGE) return true;
    }
    return false;
  }

  /** The nearest creature it can see within reach, if nearer than the fight it's in (from the shoulder at sx, sy). */
  private beastTarget(world: World, sx: number, sy: number, fight: number): { x: number; y: number } | null {
    let best = Math.min(fight, BEAST_REACT);
    let at: { x: number; y: number } | null = null;
    for (let k = 0; k < world.beasts.length; k++) {
      const b = world.beasts[k];
      // (Not a basilisk asleep: they don't know it's there, and they'd only wake it. Unless that's the job: Extermination.)
      if (!b || (wyrm(b.kind) && b.frozen > 0 && !world.exterminationLive)) continue;
      const pts = world.beastPts[k];
      for (let i = 0; i < pts.length; i += 4) {
        const d = Math.hypot(pts[i] - sx, pts[i + 1] - sy);
        if (d < best && clearLine(world, sx, sy, pts[i], pts[i + 1])) {
          best = d;
          at = { x: pts[i], y: pts[i + 1] };
        }
      }
    }
    return at;
  }

  /** Extermination: the nearest basilisk (or the dragon) left (its head), to go and kill. */
  private quarry(world: World, p: Player): { x: number; y: number } | null {
    let best = Infinity;
    let at: { x: number; y: number } | null = null;
    for (let k = 0; k < world.beasts.length; k++) {
      const q = world.beasts[k];
      if (!q || !wyrm(q.kind)) continue;
      const pts = world.beastPts[k];
      const d = Math.hypot(pts[0] - p.cx, pts[1] - p.cy);
      if (d < best) {
        best = d;
        at = { x: pts[0], y: pts[1] };
      }
    }
    return at;
  }

  private airTarget(world: World, p: Player, sx: number, sy: number, fight: number): { x: number; y: number; vx: number; vy: number } | null {
    let best: { x: number; y: number; vx: number; vy: number } | null = null;
    let bestD = Math.min(AA_RANGE, fight * 1.2);
    for (const sh of world.ships) {
      if (!sh || sh.leaving || sh.owner === p.id || (p.team !== Team.None && sh.team === p.team)) continue;
      const cx = sh.x + SHIP_W / 2;
      const cy = sh.y + SHIP_H / 2;
      if (Math.hypot(cx - sx, cy - sy) > bestD + SHIP_W) continue;
      for (let e = 0; e <= 4; e++) {
        // Pods first: a pod counts as a little nearer than the hull.
        const pod = e < 4;
        if (pod && !hasShipPart(sh.parts, ShipPart.EngineA + e)) continue;
        const q = pod ? shipPoint(sh, ENGINE_X[e], ENGINE_NOZZLE_Y - 4, this.airPt) : { x: cx, y: cy };
        const d = Math.hypot(q.x - sx, q.y - sy) * (pod ? 0.85 : 1);
        if (d >= bestD || !clearLine(world, sx, sy, q.x, q.y)) continue;
        bestD = d;
        best = { x: q.x, y: q.y, vx: sh.vx, vy: sh.vy };
      }
    }
    // An enemy watchdog or tarantula (nobody inside to shoot at: the machine itself is the target; a tarantula's chassis).
    for (const k of world.tanks) {
      if (!k || !isPet(k) || k.chute || k.owner === p.id || (p.team !== Team.None && world.players[k.owner]?.team === p.team)) continue;
      const cx = k.x + tankW(k) / 2;
      const cy = k.y + tankH(k) * (isSpider(k) ? 0.7 : 0.5);
      const d = Math.hypot(cx - sx, cy - sy);
      if (d >= bestD || !clearLine(world, sx, sy, cx, cy)) continue;
      bestD = d;
      best = { x: cx, y: cy, vx: k.vx, vy: k.vy };
    }
    return best;
  }

  think(world: World, p: Player): InputCmd {
    const t = world.tick;
    this.seq = (this.seq + 1) & 0xffff;
    const cmd: InputCmd = { seq: this.seq, buttons: 0, aim: p.aimQ, inv: invByte(p.slot, p.invVersion) };
    // Dead, or riding in: hands off (the rocket's autopilot lands it).
    if (!p.alive) {
      this.wasAlive = false;
      return cmd;
    }
    const rng = this.rng;
    if (!this.wasAlive) {
      this.wasAlive = true;
      this.holdFire = t + Math.round((45 + rng.int(45)) * skillOf(p).react); // just landed: look around first
      this.prospector = rng.next() < PROSPECT_CHANCE;
      this.myRide = -1;
      this.gold = null;
      this.badGold.length = 0;
    }

    // Pick a target: the nearest living clone, re-chosen twice a second.
    let tgt = this.target >= 0 ? world.players[this.target] : null;
    const prevTarget = this.target;
    if (!tgt || !tgt.alive || (t + this.phase) % 15 === 0) {
      this.target = -1;
      let best = Infinity;
      for (const o of world.players) {
        if (!o || o === p || !o.alive || !world.hostile(p, o)) continue;
        // (The possessed are one mind: whoever any of the hive can see, they all go for.)
        const d = ((o.cx - p.cx) ** 2 + (o.cy - p.cy) ** 2) * (p.possessed && world.hiveSees(o.id) ? 0.1 : 1);
        if (d < best) {
          best = d;
          this.target = o.id;
        }
      }
      // Regicide (and Siege) assault: the enemy king, wherever he hides (unless someone is right here).
      const king = world.kingLive && this.assault && p.team !== Team.None && !p.possessed ? world.players[world.kings[1 - p.team]] : null;
      if (king && king.alive && best > 90 * 90) this.target = king.id;
      tgt = this.target >= 0 ? world.players[this.target] : null;
      if (tgt && tgt.id !== prevTarget) this.holdFire = Math.max(this.holdFire, t + Math.round(this.react * skillOf(p).react));
    }

    if (p.tank >= 0) return this.driveTank(world, p, tgt, cmd);

    const sx = p.body.x + SHOULDER_X;
    const sy = p.body.y + SHOULDER_Y;
    // Unarmed (lost its gun)? Go and get one.
    const gunSlot = p.inv.findIndex((it) => PRIMARIES.includes(it.weapon));
    let pickup = false;
    let goalX = tgt ? tgt.cx : p.cx + this.strafe * 60;
    if (gunSlot < 0) {
      let best = 260 * 260;
      for (const it of world.items) {
        if (!PRIMARIES.includes(it.weapon)) continue;
        const d = (it.x - p.cx) ** 2 + (it.y - p.cy) ** 2;
        if (d < best) {
          best = d;
          goalX = it.x;
          pickup = d < PICKUP_R * PICKUP_R;
        }
      }
    }

    // An empty tank landed close by, and nobody to fight right here: take it.
    // (The one it called in itself, it goes for whatever the fighting, from further off.)
    const near = tgt ? Math.hypot(tgt.cx - p.cx, tgt.cy - p.cy) : Infinity;
    const mine = this.myRide >= 0 ? world.tanks[this.myRide] : null;
    if (this.myRide >= 0 && (!mine || mine.pilot !== 255 || isPet(mine))) this.myRide = -1; // (in it, someone else is, or it's gone)
    // (Under the vehicle it's going for, in a dip or a hole of its own digging: up out of it.)
    let boardUp: { x: number; y: number } | null = null;
    if ((this.tanker || this.myRide >= 0) && (near > 120 || this.myRide >= 0) && !world.extractionLive && p.tank < 0) {
      for (let slot = 0; slot < world.tanks.length; slot++) {
        const k = world.tanks[slot];
        if (!k || k.pilot !== 255 || k.chute || isPet(k) || (slot === this.abandoned && t < this.abandonUntil)) continue;
        const own = slot === this.myRide;
        if (!this.tanker && !own) continue;
        const kx = k.x + tankW(k) / 2;
        if (Math.abs(kx - p.cx) > (own ? 600 : 260) || Math.abs(k.y + tankH(k) / 2 - p.cy) > (own ? 160 : 80)) continue;
        goalX = kx;
        const dx = Math.max(k.x - (p.body.x + 8), 0, p.body.x - (k.x + tankW(k)));
        const dy = Math.max(k.y - (p.body.y + 14), 0, p.body.y - (k.y + tankH(k)));
        pickup = dx <= 8 && dy <= 8 && (t & 7) === 0;
        boardUp = Math.abs(kx - p.cx) < 40 && k.y + tankH(k) < p.body.y + ACTOR_H - 6 ? { x: kx, y: k.y + tankH(k) } : null;
        break;
      }
    }

    // Tank surfing: a friendly vehicle rolling toward a fight that's still a
    // way off is a faster, safer ride than walking: get on (the pick-up key),
    // shoot from its deck, and hop off when the fight's close, or the ride
    // stops or turns away.
    let riding = p.surf >= 0;
    if (riding) {
      const k = world.tanks[p.surf];
      const away = k && tgt ? Math.sign(tgt.cx - p.cx) * k.vx : 0;
      this.surfAway = away < -5 ? this.surfAway + 1 : 0;
      this.surfStill = k && Math.abs(k.vx) < 3 ? this.surfStill + 1 : 0;
      if (!k || near < 100 || this.surfAway > 45 || this.surfStill > 150) {
        pickup = (t & 3) === 0;
        riding = false;
      }
    } else if (!world.extractionLive && tgt && near > 220 && gunSlot >= 0 && p.tank < 0) {
      for (let slot = 0; slot < world.tanks.length; slot++) {
        const k = world.tanks[slot];
        if (!k || !world.surfable(p, k)) continue;
        const kx = k.x + tankW(k) / 2;
        // Near enough, on the way, going (or about to go) toward the fight.
        if (Math.abs(kx - p.cx) > 160 || Math.abs(k.y + tankH(k) / 2 - p.cy) > 70) continue;
        if (Math.sign(tgt.cx - kx) !== Math.sign(tgt.cx - p.cx) || k.vx * Math.sign(tgt.cx - kx) < -5) continue;
        if (world.players.filter((o) => o && o.surf === slot).length >= surfCapacity(k)) continue;
        goalX = kx;
        const dx = Math.max(k.x - (p.body.x + 8), 0, p.body.x - (k.x + tankW(k)));
        const dy = Math.max(k.y - (p.body.y + 14), 0, p.body.y - (k.y + tankH(k)));
        pickup = dx <= 12 && dy <= 12 && (t & 3) === 0;
        break;
      }
    }

    // Extraction: the objective comes first (the idol, the surface, the rocket).
    // (The possessed have no objective but the living.)
    const nav = world.extractionLive && !p.possessed ? this.extractionNav(world, p) : null;
    if (nav) {
      goalX = nav.goalX;
      if (nav.pickup) pickup = (t & 1) === 0;
    }
    // Extermination: nobody to fight but the basilisks: off to the nearest one (down the tunnels to its den, asleep).
    const quarry = !tgt && world.exterminationLive ? this.quarry(world, p) : null;
    if (quarry) goalX = quarry.x;
    if (quarry && (t + this.phase) % 4 === 0) this.seeQuarry = clearLine(world, sx, sy, quarry.x, quarry.y);
    const quarryD = quarry ? Math.hypot(quarry.x - p.cx, quarry.y - p.cy) : Infinity;
    this.quarryBlind = quarry && !this.seeQuarry && quarryD < 140 ? this.quarryBlind + 1 : 0;
    const dugIn = this.quarryBlind > 30;
    // (Rifles barely scratch one: first, a heavy gun from the cache by the tunnel mouth, if it's not far.)
    const heavySlot = world.exterminationLive ? p.inv.findIndex((it) => HEAVY.includes(it.weapon) && it.ammo > 0) : -1;
    if (quarry && heavySlot < 0 && !this.seeQuarry) {
      let best = 420 * 420;
      for (const it of world.items) {
        if (!HEAVY.includes(it.weapon)) continue;
        const d = (it.x - p.cx) ** 2 + (it.y - p.cy) ** 2;
        if (d < best) {
          best = d;
          goalX = it.x;
          pickup = d < PICKUP_R * PICKUP_R && (t & 3) === 0;
        }
      }
    }

    // Saving up for a dropship: with the fighting elsewhere, go and dig gold;
    // with enough banked, get on the radio (for itself, or its whole team).
    const near2 = tgt ? Math.hypot(tgt.cx - p.cx, tgt.cy - p.cy) : Infinity;
    // (Nearly there: it keeps its head down and digs unless they're right on it.)
    const radioSlot = p.inv.findIndex((it) => it.weapon === WeaponId.Radio);
    const airCover = world.ships.some((sh) => sh && !sh.leaving && (sh.owner === p.id || (p.team !== Team.None && sh.team === p.team)));
    // What it can have right now: one watchdog and one tarantula of its own at a time, a dropship only without air cover already up, and room for it.
    const can = (kind: number) =>
      kind === CallKind.Dropship
        ? !airCover && world.ships.includes(null)
        : world.tanks.includes(null) &&
          !(kind === CallKind.Watchdog && world.tanks.some((k) => k && isDog(k) && k.owner === p.id)) &&
          !(kind === CallKind.Tarantula && world.tanks.some((k) => k && isSpider(k) && k.owner === p.id));
    if (this.wish < 0 || !can(this.wish)) this.wish = this.pickWish(p, can);
    const cost = this.wish >= 0 ? callCost(this.wish) : Infinity;
    const quiet = !tgt || near2 > (p.gold >= cost / 2 ? 130 : 220) || (!this.seeTarget && near2 > 110);
    const calling =
      !nav && !world.extractionLive && !(world.kingLive && world.isKing(p)) && radioSlot >= 0 && this.wish >= 0 && p.gold >= cost && p.callCd === 0 && p.tank < 0 && (!tgt || near2 > 110);
    if (calling && p.weapon === WeaponId.Radio) {
      const before = world.tanks.map((k) => k !== null);
      if (world.call(p.id, this.wish)) {
        // A tank or a mole comes down empty: it's this bot's to climb into.
        if (this.wish === CallKind.Tank || this.wish === CallKind.Mole) this.myRide = world.tanks.findIndex((k, i) => k !== null && !before[i]);
        this.wish = this.pickWish(p, can);
      }
    }
    // (Not in the endgame: with only a few enemies left, it's time to finish them.)
    let foesLeft = 0;
    if (this.prospector) for (const o of world.players) if (o && o.alive && o !== p && (p.team === Team.None || o.team !== p.team)) foesLeft++;
    // (Not with a ride of its own waiting for it: that first.)
    const goldAt = !calling && this.prospector && this.myRide < 0 && quiet && (foesLeft > ENDGAME_FOES || world.phase !== Phase.Live) && !nav && !world.extractionLive && !(world.kingLive && world.isKing(p)) && p.gold < cost && gunSlot >= 0 ? this.findGold(world, p) : null;
    if (goldAt) goalX = goldAt.x;
    // A cave map, and the target somewhere else in the caves (or up top): the way there.
    const cave = !nav && !goldAt && !calling && tgt && !this.seeTarget ? this.caveNav(world, p, tgt) : null;
    if (cave) goalX = cave.goalX;
    // A target out of sight (or a long way off) is reached by a planned path
    // (nav.ts), round and over whatever won't yield, digging only through
    // what does.
    // (An enemy craft overhead it has no line on comes first: it pops up for a shot instead.)
    const skyThreat = this.airThreat(world, p);
    const route =
      !nav && !goldAt && !calling && !cave && !skyThreat && tgt && (!this.seeTarget || Math.hypot(tgt.cx - p.cx, tgt.cy - p.cy) > 160)
        ? this.pathNav(world, p, tgt.cx, tgt.body.y + ACTOR_H)
        : quarry && !calling && !skyThreat && goalX === quarry.x && quarryD > 90
          ? this.pathNav(world, p, quarry.x, quarry.y + 12)
          : null;
    if (route) goalX = route.goalX;
    else this.path = null;
    // Siege defenders hold their ground: the fortress and the ground in front
    // of it, the bolder ones out as far as the last outpost.
    // (Infection's defenders too: they hold the fortress against the hive; not the expedition, nor the possessed.)
    const sg = (world.siegeLive && p.team === DEFENDERS) || (world.infectionLive && !p.expedition && !p.possessed) ? world.siege : null;
    if (sg && !nav) {
      const west = sg.side === 0;
      const front = sg.outposts.length ? (west ? Math.max(...sg.outposts.map((o) => o[1])) : Math.min(...sg.outposts.map((o) => o[0]))) : west ? sg.fort[1] : sg.fort[0];
      const reach = this.assault ? front + (west ? 120 : -120) : west ? sg.fort[1] + 220 : sg.fort[0] - 220;
      goalX = west ? Math.max(sg.fort[0], Math.min(reach, goalX)) : Math.min(sg.fort[1], Math.max(reach, goalX));
    }

    // Moving and getting nowhere: stuck against a wall.
    const moved = Math.abs(p.body.x - this.lastX);
    this.lastX = p.body.x;
    this.stuck = moved < 0.3 ? this.stuck + 1 : Math.max(0, this.stuck - 2);

    // Which weapon: the gun, a grenade now and then up close, the digger when walled in.
    let want = gunSlot;
    const dist = tgt ? Math.hypot(tgt.cx - p.cx, tgt.cy - p.cy) : Infinity;
    const nadeSlot = p.inv.findIndex((it) => it.weapon === WeaponId.Grenade && it.ammo > 0);
    const digSlot = p.inv.findIndex((it) => it.weapon === WeaponId.Digger);
    if (nadeSlot >= 0 && dist < 150 && t > this.nadeUntil + 90 && rng.next() < 0.01) this.nadeUntil = t + 30;
    if (t < this.nadeUntil && nadeSlot >= 0) want = nadeSlot;
    // A sticky charge: up close and in sight, now and then; far more readily at someone in a vehicle (what it's for).
    const stickySlot = p.inv.findIndex((it) => it.weapon === WeaponId.Sticky);
    if (stickySlot >= 0 && tgt && this.seeTarget && dist < 130 && t > this.stickyUntil + 120 && rng.next() < (tgt.tank >= 0 ? 0.05 : 0.006)) this.stickyUntil = t + 20;
    if (t < this.stickyUntil && stickySlot >= 0) want = stickySlot;
    // Nobody about and on its feet: now and then, leave a landmine behind.
    const mineSlot = p.inv.findIndex((it) => it.weapon === WeaponId.Mine && it.ammo > 0);
    if (mineSlot >= 0 && p.body.onGround && (!tgt || (!this.seeTarget && dist > 200)) && t > this.mineUntil + 300 && rng.next() < 0.004) this.mineUntil = t + 12;
    if (t < this.mineUntil && mineSlot >= 0) want = mineSlot;
    // Up close with a launcher: a gun that won't blow us up too, if we carry one.
    // A basilisk in its sights (and not right on top of it): the heavy gun.
    if (heavySlot >= 0 && !tgt && this.beast && Math.hypot(this.beast.x - sx, this.beast.y - sy) > 60 && t >= this.nadeUntil) want = heavySlot;
    const splashy = p.inv[want]?.weapon === WeaponId.Bazooka || p.inv[want]?.weapon === WeaponId.GrenadeLauncher;
    if (dist < 70 && splashy) {
      const safe: number[] = [WeaponId.Shotgun, WeaponId.Smg, WeaponId.Gatling, WeaponId.Rifle, WeaponId.LightRifle, WeaponId.Blaster, WeaponId.Laser, WeaponId.Sniper];
      const gun = p.inv.findIndex((it) => safe.includes(it.weapon));
      if (gun >= 0) want = gun;
    }
    // Out of sight but close, or straight above/below (a roof or floor away,
    // however high): counts toward digging to it.
    this.blind = tgt && !this.seeTarget && (dist < 160 || Math.abs(tgt.cx - p.cx) < 60) ? this.blind + 1 : 0;
    // Walled in, or a target close by on the other side of a floor or wall: dig to it.
    // Headroom: open sky (or room) above means a wall can be jumped or jetted over.
    const bx0 = Math.floor(p.body.x);
    const by0 = Math.floor(p.body.y);
    const headClear = !world.terrain.rectSolid(bx0, by0 - 22, bx0 + 7, by0 - 1);
    // (The labyrinth's stone never yields: no digging through it.)
    // Close enough to the gold to bite at it (or tunnelling down to it).
    // Mining: once over the seam, sink a shaft straight down to it (or
    // just bite at it when it's within the beam's reach).
    const gdx = goldAt ? goldAt.x - p.cx : 0;
    const gdy = goldAt ? goldAt.y - sy : 0;
    const mining = !!goldAt && digSlot >= 0 && (Math.hypot(goldAt.x - sx, gdy) < DIGGER_REACH + 6 || (Math.abs(gdx) < 6 && gdy > 0));
    if (mining) this.stuck = 0; // (standing still on purpose: not stuck)
    // Digging and getting nothing (rock or steel in the way): give that seam up and find another.
    if (p.gold !== this.goldBanked || !mining) {
      this.goldBanked = p.gold;
      this.goldGainAt = t;
    } else if (goldAt && t - this.goldGainAt > GOLD_STALL) {
      this.badGold.push(goldAt);
      if (this.badGold.length > 8) this.badGold.shift();
      this.gold = null;
      this.goldScanAt = -1e9;
      this.goldGainAt = t;
    }
    // (Never at what won't yield: the caltrops' cement, the ruins' masonry, pig iron. It goes round those.)
    const toward = this.blind > 60 && tgt ? { x: tgt.cx, y: tgt.cy } : dugIn && quarry ? quarry : { x: p.cx + (Math.sign(goalX - p.cx) || 1) * 24, y: p.cy };
    const hardWay = hardBetween(world, sx, sy, toward.x, toward.y);
    // (Its vehicle overhead, a roof between: dig up to it.)
    const digUp = !!boardUp && !headClear && !hardBetween(world, sx, sy, boardUp.x, boardUp.y);
    const digging = !world.extractionLive && digSlot >= 0 && (mining || !!route?.dig || (!this.seeTarget && !hardWay && (this.stuck > 75 || this.blind > 60)) || (dugIn && !hardWay) || digUp);
    if (digging) want = digSlot;
    if (calling) want = radioSlot;
    if (want < 0) want = digSlot >= 0 ? digSlot : p.slot;
    // Hurt or maimed, with nobody shooting back right now: set off the repair kit (once: it's spent).
    const kitSlot = p.inv.findIndex((it) => it.weapon === WeaponId.RepairKit);
    const healing = kitSlot >= 0 && !digging && (p.hp < 55 || stumps(p.parts.mask, p.parts.cls) > 0 || (p.parts.cls === ClassId.Droid && p.parts.mask !== DROID_MASK)) && (!tgt || !this.seeTarget || dist > 200);
    if (healing && !calling) want = kitSlot;
    const weapon = p.inv[want]?.weapon ?? WeaponId.Digger;

    // Line of sight to the target, every few ticks.
    if (tgt && (t + this.phase) % 4 === 0) this.seeTarget = clearLine(world, sx, sy, tgt.cx, tgt.cy, Math.max(MAX_SHOT, skillOf(p).range));

    // Movement: close to fighting range, back off if too close, strafe in between.
    let buttons = 0;
    // (Possessed: it closes right in, to finish them with its claws.)
    const range = p.possessed ? 0 : (RANGE[weapon] ?? 120);
    const dx = goalX - p.cx;
    let dir = 0;
    // On the objective unless someone's right in our face (a carrier never stops to brawl).
    const onTask = nav && !(tgt && this.seeTarget && dist < 100 && !nav.urgent);
    if (onTask) dir = Math.abs(dx) < 2.5 || nav.fall ? 0 : Math.sign(dx);
    else if (cave) dir = Math.abs(dx) < 2.5 ? 0 : Math.sign(dx);
    else if (route) dir = Math.abs(dx) < 2.5 ? 0 : Math.sign(dx);
    else if (goldAt) dir = Math.abs(gdx) < 3 || (mining && Math.abs(gdx) < 6) ? 0 : Math.sign(gdx);
    else if (calling) dir = 0;
    else if (gunSlot < 0 || !tgt) dir = Math.sign(dx);
    else if (weapon === WeaponId.Digger) dir = Math.sign(dx);
    else if (dist > range + 30 || !this.seeTarget) dir = Math.sign(dx);
    else if (dist < range - 50) dir = -Math.sign(dx);
    else {
      if (t > this.strafeUntil) {
        this.strafe = rng.next() < 0.5 ? -1 : 1;
        this.strafeUntil = t + 20 + rng.int(40);
      }
      dir = this.strafe;
    }
    // Walking toward it and getting no nearer for seconds (wedged under an
    // overhang, a caltrop's arm, a face too tall to jet): back off and jet
    // up for a moment, then come at it again from higher up.
    const closing = !onTask && !cave && !route && !goldAt && !calling && !mining && dir !== 0 && dir === Math.sign(dx) && Math.abs(dx) > 60;
    if (!closing && t >= this.detourUntil) {
      this.walkBest = Infinity;
      this.walkAt = t;
    } else if (closing && Math.abs(dx) < this.walkBest - 12) {
      this.walkBest = Math.abs(dx);
      this.walkAt = t;
    } else if (closing && t - this.walkAt > 150) {
      this.detourDir = -dir;
      this.detourUntil = t + 30 + rng.int(40);
      this.walkBest = Infinity;
      this.walkAt = this.detourUntil;
    }
    const detour = t < this.detourUntil;
    if (detour) dir = this.detourDir;
    if (dir > 0) buttons |= BTN_RIGHT;
    if (dir < 0) buttons |= BTN_LEFT;
    // Jump or jet: over walls, up to a target above, and to break a long fall.
    const b = p.body;
    if ((detour || t < this.detourUntil + 45) && b.fuel > 20) buttons |= BTN_UP;
    if (dir !== 0 && this.stuck > 6 && headClear) buttons |= BTN_UP;
    if (onTask) {
      if (nav.up) buttons |= BTN_UP;
    } else if (cave) {
      if (cave.up) buttons |= BTN_UP;
    } else if (route) {
      if (route.up && b.fuel > 4) buttons |= BTN_UP;
    } else if (boardUp && b.fuel > (b.onGround ? 40 : 5) && (headClear || !world.terrain.rectSolid(bx0, by0 - 5, bx0 + 7, by0 - 1))) buttons |= BTN_UP; // (up into the hole it's digging, too)
    else if (tgt && tgt.cy < p.cy - 50 && b.fuel > 35) buttons |= BTN_UP;
    if (b.vy > 260 && b.fuel > 5) buttons |= BTN_UP;
    // An enemy dropship or drone overhead with no line on it from here: up on the jetpack for one.
    if (skyThreat && !this.air && b.fuel > 30 && headClear) buttons |= BTN_UP;

    // Aim: lead the target by the shot's flight time, lift lobbed shots, add skill noise.
    let ax = goalX;
    let ay = p.cy;
    const def = WEAPONS[weapon];
    if (tgt && def) {
      const speed = def.speed || 400;
      const lead = dist / speed;
      const sk = skillOf(p).lead;
      ax = tgt.cx + tgt.body.vx * lead * sk;
      ay = tgt.cy + tgt.body.vy * lead * 0.5 * sk;
      if (def.proj >= 0) ay -= 0.5 * GRAVITY * PROJ[def.proj].gravity * lead * lead;
    }
    let sweep = 0;
    if (weapon === WeaponId.Digger && mining && goldAt) {
      const tl = Math.hypot(goldAt.x - sx, gdy) || 1;
      if (tl > DIGGER_REACH + 6) {
        // Over it, out of reach: sink a shaft straight down, the beam swept
        // a little either way so the shaft's wide enough to drop down.
        ax = p.cx + Math.sin(t * 0.5) * 6;
        ay = sy + DIGGER_REACH;
      } else {
        // In reach: straight at the gold; the beam bites the first solid cell on the way.
        ax = sx + ((goldAt.x - sx) / tl) * DIGGER_REACH;
        ay = sy + (gdy / tl) * DIGGER_REACH;
      }
    } else if (weapon === WeaponId.Digger) {
      // Dig at the target when it's just the other side of a floor or wall;
      // otherwise through the obstacle in the way.
      let tx = (tgt ? tgt.cx : digUp && boardUp ? boardUp.x : dugIn && quarry ? quarry.x : goalX) - p.cx;
      let ty = (tgt ? tgt.cy : digUp && boardUp ? boardUp.y : dugIn && quarry ? quarry.y : p.cy) - p.cy;
      if (route?.dig) {
        // On its path: through the soft ground the path runs into.
        tx = route.digX - p.cx;
        ty = route.digY - p.cy;
      } else if (this.blind <= 60 && !dugIn && !digUp) {
        // Blocked on the way somewhere: straight through whatever is ahead.
        tx = Math.sign(tx) || 1;
        ty = 0;
      }
      const tl = Math.hypot(tx, ty) || 1;
      ax = sx + (tx / tl) * 20;
      ay = sy + 3 + (ty / tl) * 20; // from mid-body
      // Boxed in for a while (leftover pixels of concrete or rock pinning
      // it): clear the nearest solid cell around the body, leaning toward
      // where it's going, never the floor under its feet.
      if (this.stuck > 150) {
        const near = nearestBlock(world, p, Math.sign(dx) || 1);
        if (near) {
          ax = near.x;
          ay = near.y;
          sweep = 0;
        }
      }
      // Sweep the beam up and down so the hole is tall enough to walk through
      // (in concrete only the beam's core bites).
      if (this.stuck <= 150) sweep = Math.sin(t * 0.35) * 0.65;
    }
    // Anti-air: an enemy dropship overhead, nearer than whoever it's fighting: shoot it down.
    const antiAir = !digging && !healing && !calling && !!def && ANTI_AIR.includes(weapon);
    if (!antiAir) this.air = null;
    else if ((t + this.phase) % 6 === 0) this.air = this.airTarget(world, p, sx, sy, tgt && this.seeTarget ? dist : Infinity);
    const air = this.air;
    // Wildlife coming at it, nearer than whoever it's fighting: that first (any gun will do).
    const armed = !digging && !healing && !calling && !!def && (def.proj >= 0 || weapon === WeaponId.Laser) && weapon !== WeaponId.Mine;
    // (The possessed leave the wildlife be: none of it ever goes for them, and the infected are the hive's own.)
    if (!armed || air || p.possessed) this.beast = null;
    else if ((t + this.phase) % 5 === 0) this.beast = this.beastTarget(world, sx, sy, tgt && this.seeTarget ? dist : Infinity);
    const beast = this.beast;
    if (beast && def && !air) {
      const lead = Math.hypot(beast.x - sx, beast.y - sy) / (def.speed || 400);
      ax = beast.x;
      ay = beast.y;
      if (def.proj >= 0) ay -= 0.5 * GRAVITY * PROJ[def.proj].gravity * lead * lead;
      sweep = 0;
    }
    if (air && def) {
      const lead = Math.hypot(air.x - sx, air.y - sy) / (def.speed || 400);
      ax = air.x + air.vx * lead;
      ay = air.y + air.vy * lead;
      if (def.proj >= 0) ay -= 0.5 * GRAVITY * PROJ[def.proj].gravity * lead * lead;
      sweep = 0;
    }
    // (A sticky charge is lobbed: aim high enough for its arc to come down on them.)
    if (weapon === WeaponId.Sticky && !air && !beast) {
      const lead = Math.hypot(ax - sx, ay - sy) / STICKY_THROW;
      ay -= 0.5 * GRAVITY * lead * lead;
    }
    // (Possessed without its guns: it looks straight at them, to lunge and vomit.)
    const lunger = p.possessed && !p.armed && weapon !== WeaponId.Digger;
    const aim = lunger && tgt ? Math.atan2(tgt.cy - sy, tgt.cx - sx) : Math.atan2(ay - sy, ax - sx) + sweep + (rng.next() - 0.5) * 2 * this.noise * skillOf(p).noise;
    cmd.aim = quantizeAim(aim);

    // Fire: with a clear line (or digging), in range; semi-auto weapons get a fresh press each shot.
    // No point-blank blasts, unless cornered with nothing else.
    const minRange = this.stuck > 30 ? 0 : weapon === WeaponId.Bazooka ? 70 : weapon === WeaponId.Grenade || weapon === WeaponId.GrenadeLauncher ? 50 : 0;
    const shoot = lunger
      ? tgt !== null && this.seeTarget && dist > 14 && dist < LUNGE_REACH && Math.abs(tgt.cy - p.cy) < 60
      : air || beast
      ? t >= this.holdFire
      : weapon === WeaponId.Mine
        ? t < this.mineUntil && t > this.mineUntil - 8
        : weapon === WeaponId.Sticky
        ? t < this.stickyUntil && tgt !== null && this.seeTarget && dist < 160
        : weapon === WeaponId.RepairKit
        ? healing
        : weapon === WeaponId.Digger
        ? digging
        : tgt !== null && t >= this.holdFire && this.seeTarget && dist < skillOf(p).range && dist > minRange && !(p.possessed && dist < 45) && (weapon !== WeaponId.Grenade || dist < 220) && !(skillOf(p).pause > 0 && rng.next() < skillOf(p).pause);
    if (weapon === WeaponId.Laser) {
      // Laser: hold to charge (longer the further off they are), then let go to fire.
      if (shoot && p.charge < this.laserGoal) buttons |= BTN_FIRE;
      else if (p.charge === 0) this.laserGoal = Math.min(LASER_MAX, 12 + Math.floor(dist / 3) + rng.int(40));
    } else if (shoot) {
      this.trigger = def?.auto || lunger ? true : !this.trigger;
      if (this.trigger) buttons |= BTN_FIRE;
    } else this.trigger = false;

    // A king holds his vault: he turns and fights, but never leaves it.
    if (world.kingLive && world.isKing(p)) {
      buttons &= ~(BTN_LEFT | BTN_RIGHT | BTN_UP);
      if (weapon === WeaponId.Digger) buttons &= ~BTN_FIRE;
      if (want === digSlot && gunSlot >= 0) want = gunSlot;
      pickup = false;
    }
    // Riding: feet still (a jump would take it off), the guns as ever.
    if (riding) buttons &= ~(BTN_LEFT | BTN_RIGHT | BTN_UP);
    cmd.buttons = buttons;
    cmd.inv = invByte(want >= 0 ? want : p.slot, p.invVersion, pickup);
    return cmd;
  }

  /**
   * Driving: keep the target at cannon range, jet over whatever blocks the
   * treads, hose it with the vulcan and lob shells at it. A tank that gets
   * nowhere for long enough is abandoned (out of the hatch, on foot again).
   */
  /**
   * Extraction: where to go next. Carrying the idol, out by the nearest well
   * and on to the extraction rocket; a teammate carrying it, stay with them;
   * otherwise after the idol (on its altar, dropped, or in an enemy's hands).
   * Through the labyrinth it follows a breadth-first route cell by cell:
   * sideways through doorways, down by dropping through a floor shaft, up by
   * jetting through one (resting on a ledge when the jetpack runs low).
   */
  private extractionNav(world: World, p: Player): { goalX: number; up: boolean; fall: boolean; pickup: boolean; carrying: boolean; urgent: boolean } | null {
    const d = world.dungeon;
    if (!d) return null;
    const b = p.body;
    const holder = world.idolHolder();
    const carrying = holder === p;
    let tx = p.cx;
    let ty = p.cy;
    let pickup = false;
    let urgent = carrying;
    if (holder && !carrying) {
      tx = holder.cx;
      ty = holder.body.y;
    } else if (!holder) {
      const it = world.items.find((o) => o.weapon === WeaponId.Idol);
      if (it) {
        tx = it.x;
        ty = it.y - ACTOR_H + 1;
        const d2 = Math.hypot(it.x - p.cx, it.y - p.cy);
        pickup = d2 < PICKUP_R - 2;
        urgent = d2 < 80; // it's right there: grab it, fight later
      }
    }
    const wantSurface = carrying || cellOfFeet(tx, ty) < 0;
    const wells = d.entrances.slice(0, 4);
    const here = cellOfFeet(p.cx, b.y);
    const out = { goalX: tx, up: false, fall: false, pickup, carrying, urgent };

    if (here < 0) {
      // Up top: the desert, or inside a well.
      let well = wells[0];
      for (const w of wells) if (Math.abs(w.x - p.cx) < Math.abs(well.x - p.cx)) well = w;
      const inWell = Math.abs(p.cx - well.x) < SHAFT_HALF + 2 && b.y + ACTOR_H > well.top - 1;
      if (wantSurface) {
        if (inWell) {
          // Climb out of the well, then step off its mouth.
          out.goalX = b.y + ACTOR_H > well.top - 3 ? well.x : well.x + (p.cx < well.x ? -40 : 40);
          out.up = b.y + ACTOR_H > well.top - 6;
          return out;
        }
        if (carrying) {
          const e = world.evac;
          out.goalX = e.state === Evac.Landed ? e.x : p.cx;
        } else out.up = ty < b.y - 20 && b.fuel > 20; // up to wherever it is
        return out;
      }
      // Down the nearest well (the pyramid's shaft is a long climb back).
      out.goalX = well.x;
      out.fall = Math.abs(p.cx - well.x) < 3;
      return out;
    }

    // In the labyrinth: route to the goal cell(s).
    const goals = wantSurface ? wells.map((w) => w.c) : [cellOfFeet(tx, ty)];
    const key = goals.join(',');
    if (key !== this.navKey || world.tick - this.navAt > 45) {
      this.navDist = mazeDistances(d, goals);
      this.navKey = key;
      this.navAt = world.tick;
    }
    const dist = this.navDist!;
    const r = Math.floor(here / COLS);
    const centre = cellCentreX(here);
    const climb = () => {
      // Rest on the floor beside the shaft when low, then go up its middle.
      if (this.resting && b.fuel > 90) this.resting = false;
      if (!this.resting && b.fuel < 18) this.resting = true;
      if (this.resting) {
        out.goalX = centre + (p.cx < centre ? -SHAFT_HALF - 7 : SHAFT_HALF + 7);
        return out;
      }
      out.goalX = centre;
      out.up = Math.abs(p.cx - centre) < SHAFT_HALF - 4 || inShaftUnder(b.y, r);
      return out;
    };
    if (wantSurface && dist[here] === 0) return climb(); // up the well
    if (dist[here] === 0) {
      out.goalX = tx;
      out.up = ty < b.y - 24 && b.fuel > 20; // up on a ledge in the same hall
      return out;
    }
    const nxt = nextHop(d, dist, here);
    if (nxt < 0) return out;
    if (nxt === here + COLS) {
      out.goalX = centre;
      out.fall = Math.abs(p.cx - centre) < 3;
      return out;
    }
    if (nxt === here - COLS) return climb();
    this.resting = false;
    if (inShaftUnder(b.y, r)) {
      // Still coming up through the floor: clear it first.
      out.goalX = centre;
      out.up = true;
      return out;
    }
    out.goalX = cellCentreX(nxt);
    return out;
  }

  /**
   * A cave map (caves.ts), its target on another level (the surface, the
   * highway, the deep run) and out of sight: which way to go. Up a level by
   * the nearest way up from here (a ramp or link passage, walked; a shaft,
   * jetted up its middle, resting on the floor beside it when the jetpack
   * runs low); down by the nearest passage, or by dropping down a shaft it's
   * standing over. Halfway along a passage or up a shaft, it carries on.
   */
  /**
   * Follow a planned path (nav.ts) toward (tx, ty): replanned every second
   * and a half or so, or when the target has moved off. The way on is a few waypoints
   * ahead; up there means the jetpack; soft ground on it means the digger.
   */
  private pathNav(world: World, p: Player, tx: number, ty: number, veh?: Tank): { goalX: number; up: boolean; dig: boolean; digX: number; digY: number } | null {
    const t = world.tick;
    // (Driving, the vehicle is what goes: its size, its jets' fuel.)
    const at = veh ? { cx: veh.x + tankW(veh) / 2, cy: veh.y + tankH(veh) / 2 } : p;
    const feet = veh ? veh.y + tankH(veh) : p.body.y + ACTOR_H;
    const fuel = veh ? veh.fuel : p.body.fuel;
    const body = veh ? vehicleBody(veh) : undefined;
    if (!!veh !== this.pathVeh) {
      this.path = null;
      this.pathVeh = !!veh;
    }
    const half = veh ? tankW(veh) / 2 : 0;
    // Stuck on its path (wedged in a notch the plan didn't see): off it a moment (the stuck-detour takes over), then plan afresh.
    if (t < this.offRouteUntil) return null;
    if (Math.abs(at.cx - this.routeX) > 6 || Math.abs(at.cy - this.routeY) > 6) {
      if (this.avoid && Math.hypot(at.cx - this.avoid.x, at.cy - this.avoid.y) > 60) this.stalls = 0; // (well clear of it: unstuck)
      this.routeX = at.cx;
      this.routeY = at.cy;
      this.routeAt = t;
    } else if (t - this.routeAt > 90) {
      // Stuck: plan again round this spot; stuck again, off the path a moment first.
      const pts = this.path?.pts;
      const nxt = pts ? pts[Math.min(pts.length - 1, this.pathIdx + 1)] : null;
      this.avoid = { x: nxt ? nxt.x : at.cx, y: nxt ? nxt.y - 7 : at.cy, r: 20 };
      this.path = null;
      this.routeAt = t;
      if (++this.stalls >= 2) {
        this.stalls = 0;
        this.offRouteUntil = t + 60;
        return null;
      }
    }
    const stale = !this.path || t - this.pathAt > 45 || Math.hypot(tx - this.pathTo.x, ty - this.pathTo.y) > 96 || this.pathIdx >= this.path.pts.length - 2;
    // (The planning is shared out: a few searches a tick across all the bots; the rest keep to their old
    // paths a moment longer, or with none yet, head straight for it till their turn comes.)
    // (A vehicle's search costs two of the tick's searches.)
    if (stale && navBudget(world) && (!veh || navBudget(world))) {
      this.path = findPath(world.terrain, at.cx, feet, tx, ty, veh ? 3500 : undefined, this.avoid, body);
      this.pathAt = t + (this.phase % 15); // (staggered: the bots don't all plan on one tick)
      this.pathIdx = 0;
      this.pathTo = { x: tx, y: ty };
    }
    if (!this.path) return null;
    const pts = this.path.pts;
    if (pts.length < 2) return null;
    // Along it as far as it has come: the nearest waypoint over the next few.
    let bestD = Infinity;
    for (let k = this.pathIdx; k < Math.min(pts.length, this.pathIdx + 8); k++) {
      const d = Math.max(0, Math.abs(pts[k].x - at.cx) - half) + Math.abs(pts[k].y - feet) * 0.7;
      if (d <= bestD) {
        bestD = d;
        this.pathIdx = k;
      }
    }
    // The jetpack, in bursts: let it refill when it runs dry.
    if (this.resting && fuel > 60) this.resting = false;
    if (!this.resting && fuel < 6) this.resting = true;
    const next = pts[Math.min(pts.length - 1, this.pathIdx + 1)];
    const ahead = pts[Math.min(pts.length - 1, this.pathIdx + 3)];
    return {
      goalX: ahead.x,
      up: !this.resting && Math.min(next.y, ahead.y) < feet - (veh ? TANK_STEP_UP : 4),
      dig: next.dig || ahead.dig,
      digX: next.dig ? next.x : ahead.x,
      digY: (next.dig ? next.y : ahead.y) - 7,
    };
  }

  private caveNav(world: World, p: Player, tgt: Player): { goalX: number; up: boolean } | null {
    const net = world.caves;
    if (!net) return null;
    const b = p.body;
    const feet = b.y + ACTOR_H;
    const me = caveLevel(net, p.cx, feet);
    const it = caveLevel(net, tgt.cx, tgt.body.y + ACTOR_H);
    const up = it < me || (it === me && tgt.cy < p.cy - 80);
    // Partway along a passage: on to its far end.
    for (const r of [...net.ramps, ...net.links]) {
      if (!onPassage(r, p.cx, feet)) continue;
      const end = up ? r.top : r.bottom;
      const dir = Math.sign(end.x - (up ? r.bottom.x : r.top.x));
      if (Math.abs(p.cx - end.x) > 6) return { goalX: end.x + dir * 30, up: false };
    }
    // In a shaft: up its middle (resting when low), or down it.
    for (const s of net.shafts) {
      if (Math.abs(p.cx - s.x) > 14 || feet <= s.top + 2 || feet >= s.bottom - 2) continue;
      if (this.resting && b.fuel > 90) this.resting = false;
      if (!this.resting && b.fuel < 12) this.resting = true;
      return { goalX: s.x, up: up && !this.resting };
    }
    if (me === it) return null;
    // The ways off this level, up or down, nearest first (weighed toward the target).
    let best: { goalX: number; up: boolean } | null = null;
    let bestD = Infinity;
    const consider = (x: number, go: { goalX: number; up: boolean }) => {
      const d = Math.abs(x - p.cx) + Math.abs(x - tgt.cx) * 0.5;
      if (d < bestD) {
        bestD = d;
        best = go;
      }
    };
    const passages: Ramp[] = me === 2 || (me === 1 && !up) ? net.links : net.ramps;
    for (const r of passages) {
      const from = up ? r.bottom : r.top;
      if (caveLevel(net, from.x, from.y) !== me && !(me === 0 && !up)) continue;
      consider(from.x, { goalX: from.x, up: false });
    }
    for (const s of net.shafts) {
      // Up: from the tunnel at its foot. Down: only standing in the room it opens out of.
      if (up && caveLevel(net, s.x, s.bottom) === me) {
        const under = Math.abs(p.cx - s.x) < 10;
        if (this.resting && b.fuel > 90) this.resting = false;
        if (!this.resting && b.fuel < 12) this.resting = true;
        consider(s.x, { goalX: this.resting ? s.x + 24 : s.x, up: under && !this.resting });
      } else if (!up && Math.abs(feet - s.top) < 8 && Math.abs(p.cx - s.x) < 80) consider(s.x, { goalX: s.x, up: false });
    }
    return best;
  }

  /**
   * At the controls of a tank or a mole. What it goes for: whoever it's
   * fighting; with nobody (Extermination), the nearest basilisk. Out of
   * sight or a long way off, it follows a path planned for the vehicle's
   * own size (nav.ts): down chutes and tunnels, up on its jets where the
   * treads can't climb, shelling (a mole: burning) through soft ground where
   * that's shorter than the way round. In sight and in range, it stands off
   * and fights. Stuck for good, it shells a way through; still stuck, it
   * bails out.
   */
  private driveTank(world: World, p: Player, tgt: Player | null, cmd: InputCmd): InputCmd {
    const t = world.tick;
    const k = world.tanks[p.tank];
    if (!k) return cmd;
    const w = tankW(k);
    const cx = k.x + w / 2;
    const cy = k.y + 6;
    const bottom = k.y + tankH(k);
    const moved = Math.abs(k.x - this.lastX) + Math.abs(k.y - this.lastY) * 0.5;
    this.lastX = k.x;
    this.lastY = k.y;
    let buttons = 0;
    // What it's after: a clone, or the quarry (a basilisk's head).
    let foe: { x: number; y: number; vx: number; vy: number; feet: number } | null = tgt ? { x: tgt.cx, y: tgt.cy, vx: tgt.body.vx, vy: tgt.body.vy, feet: tgt.body.y + ACTOR_H } : null;
    if (!foe && world.exterminationLive) {
      const q = this.quarry(world, p);
      if (q) foe = { x: q.x, y: q.y, vx: 0, vy: 0, feet: q.y + 12 };
    }
    if (!foe) {
      this.tankStuck = 0;
      this.path = null;
      cmd.buttons = 0;
      return cmd;
    }
    const dx = foe.x - cx;
    const dist = Math.hypot(dx, foe.y - cy);
    if ((t + this.phase) % 4 === 0) this.seeTarget = clearLine(world, cx, cy - 4, foe.x, foe.y);
    // Out of sight, or a long way off: the planned way there.
    const route = !this.seeTarget || dist > 300 ? this.pathNav(world, p, foe.x, foe.feet, k) : null;
    if (!route) this.path = null;
    let goalX = route ? route.goalX : foe.x;
    // Siege defenders keep to the fortress and the ground before it.
    const sg = (world.siegeLive && p.team === DEFENDERS) || (world.infectionLive && !p.expedition && !p.possessed) ? world.siege : null;
    if (sg) {
      const west = sg.side === 0;
      goalX = west ? Math.max(sg.fort[0], Math.min(sg.fort[1] + 220, goalX)) : Math.min(sg.fort[1], Math.max(sg.fort[0] - 220, goalX));
    }
    const gdx = goalX - cx;
    const dir = route ? (Math.abs(gdx) > 3 ? Math.sign(gdx) : 0) : Math.abs(dx) > 220 ? Math.sign(dx) : Math.abs(dx) < 90 ? -Math.sign(dx) : 0;
    if (dir > 0) buttons |= BTN_RIGHT;
    if (dir < 0) buttons |= BTN_LEFT;
    // Stuck means no progress toward where it's going over a couple of seconds
    // (rocking against a face it can't climb still counts as stuck).
    if (dir === 0 && !route) this.tankStuck = 0;
    else if (moved < 0.3) this.tankStuck++;
    if ((t + this.phase) % 60 === 0) {
      const d = route ? Math.hypot(foe.x - cx, foe.y - cy) : Math.abs(dx);
      if ((dir !== 0 || route) && d > this.progD - 10) this.tankStuck = Math.max(this.tankStuck, 60) + 30;
      else if (d < this.progD - 10) this.tankStuck = 0;
      this.progD = d;
    }
    // Jets: up where the route climbs, over what the treads can't take, up to a foe overhead.
    if ((route?.up || this.tankStuck > 8 || (!route && foe.y < cy - 60)) && k.fuel > (route?.up ? 0 : 20)) buttons |= BTN_UP;
    // (Over the drop its route goes down: no jets, let it fall.)
    if (route && !route.up && this.path && this.pathIdx + 1 < this.path.pts.length && this.path.pts[this.pathIdx + 1].y > bottom + 12) buttons &= ~BTN_UP;
    // Lead with the vulcan's flight time; shells drop, so lift them.
    const lead = dist / SMG_SPEED;
    const ax = foe.x + foe.vx * lead;
    const ay = foe.y + foe.vy * lead * 0.5;
    cmd.aim = quantizeAim(Math.atan2(ay - cy, ax - cx) + (this.rng.next() - 0.5) * 2 * this.noise);
    // Close but out of sight (a floor or wall between): shell toward it to
    // open a way; still nothing after a while, bail out and dig.
    this.blind = !this.seeTarget && dist < 240 ? this.blind + 1 : 0;
    // Soft ground on its route: shell (burn) a way through, at where the route goes into it.
    const digAt = route?.dig ? { x: route.digX, y: route.digY - tankH(k) / 2 + 7 } : null;
    const fight = t >= this.holdFire && this.seeTarget && dist < 380;
    if (isMole(k)) {
      // A mole: the SMG at range, the flamethrower up close; blocked or blind, it burns its way through.
      if (fight) {
        buttons |= BTN_FIRE;
        if (dist < 105) buttons |= BTN_SCOPE;
      } else if (digAt) {
        cmd.aim = quantizeAim(Math.atan2(digAt.y - cy, digAt.x - cx));
        buttons |= BTN_SCOPE;
      } else if (this.blind > 30 || (this.tankStuck > 30 && dir !== 0)) {
        cmd.aim = quantizeAim(this.blind > 30 ? Math.atan2(foe.y - cy, foe.x - cx) : dir > 0 ? 0.15 : Math.PI - 0.15);
        buttons |= BTN_SCOPE;
      }
    } else if (fight) {
      buttons |= BTN_FIRE;
      const sl = dist / CANNON_SPEED;
      const lift = 0.5 * GRAVITY * PROJ[4].gravity * sl * sl;
      if (dist > 60 && Math.abs(Math.atan2(ay - lift - cy, Math.abs(ax - cx))) < 1.1) {
        cmd.aim = quantizeAim(Math.atan2(ay - lift - cy, ax - cx));
        buttons |= BTN_SCOPE;
      }
    } else if (digAt && Math.hypot(digAt.x - cx, digAt.y - cy) < 90 && k.cannonCd <= 0) {
      cmd.aim = quantizeAim(Math.atan2(digAt.y - cy, digAt.x - cx));
      buttons |= BTN_SCOPE;
    } else if (this.blind > 60 && k.cannonCd <= 0) {
      cmd.aim = quantizeAim(Math.atan2(foe.y - cy, foe.x - cx));
      buttons |= BTN_SCOPE;
    } else if (this.tankStuck > 45 && dir !== 0) {
      // Treads and jets get nowhere: shell a way through, ahead and a little down.
      cmd.aim = quantizeAim(dir > 0 ? 0.25 : Math.PI - 0.25);
      buttons |= BTN_SCOPE;
    }
    cmd.buttons = buttons;
    const bail = (this.tankStuck > 300 || this.blind > 360) && (t & 7) === 0;
    if (bail) {
      this.tankStuck = 0;
      this.blind = 0;
      this.abandoned = p.tank;
      this.abandonUntil = t + 30 * 30;
    }
    cmd.inv = invByte(p.slot, p.invVersion, bail);
    return cmd;
  }
}

/** A vehicle's body for the path planner: its size in nodes; shelling through soft ground dear (a mole burns through it cheaply), jets dearer than a jetpack. */
function vehicleBody(k: Tank): NavBody {
  // (Greedy: each node it weighs checks its whole footprint, so it searches far fewer, for a route a little longer than the best.)
  return { w: Math.ceil(tankW(k) / NAV_CELL), h: Math.ceil(tankH(k) / NAV_CELL), dig: isMole(k) ? 3 : 10, climb: 2, hang: 0.7, fall: true, greed: 1.4 };
}

/** Is the straight line between two points free of terrain? (2-cell steps) */
function clearLine(world: World, x0: number, y0: number, x1: number, y1: number, max = MAX_SHOT): boolean {
  const dx = x1 - x0;
  const dy = y1 - y0;
  const len = Math.sqrt(dx * dx + dy * dy);
  if (len > max) return false;
  const n = Math.ceil(len / 2);
  const t = world.terrain;
  for (let i = 2; i < n; i++) {
    if (t.isSolid(Math.floor(x0 + (dx * i) / n), Math.floor(y0 + (dy * i) / n))) return false;
  }
  return true;
}

/** The solid cell nearest a clone's body (biased toward `dir`), excluding the floor under it. */
/** Is there material on the way from (x0, y0) toward (x1, y1) (the first 24 cells) that a digger won't get through? */
function hardBetween(world: World, x0: number, y0: number, x1: number, y1: number): boolean {
  const d = Math.hypot(x1 - x0, y1 - y0) || 1;
  const n = Math.min(24, Math.ceil(d));
  for (let k = 2; k <= n; k += 2) {
    const m = world.terrain.get(Math.round(x0 + ((x1 - x0) / d) * k), Math.round(y0 + ((y1 - y0) / d) * k));
    if (m !== Mat.Air && undiggable(m)) return true;
  }
  return false;
}

function nearestBlock(world: World, p: Player, dir: number): { x: number; y: number } | null {
  const t = world.terrain;
  const bx = Math.floor(p.body.x);
  const by = Math.floor(p.body.y);
  let best: { x: number; y: number } | null = null;
  let bestD = Infinity;
  for (let y = by - 6; y <= by + 13; y++) {
    for (let x = bx - 7; x <= bx + 14; x++) {
      if (!t.isSolid(x, y)) continue;
      const dx = x - (bx + 4);
      const dy = y - (by + 7);
      const d = dx * dx + dy * dy - dir * dx * 4;
      if (d < bestD) {
        bestD = d;
        best = { x: x + 0.5, y: y + 0.5 };
      }
    }
  }
  return best;
}
